import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Slider } from 'primereact/slider';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import './WaitTimesFlushManager.css';

const DAY_PRESETS = [7, 14, 30, 60, 90];

function formatCount(n) {
  const value = Number(n) || 0;
  return value.toLocaleString('es-MX');
}

function formatDays(n) {
  const value = Number(n);
  if (!Number.isFinite(value) || value <= 0) return '0 días';
  if (value < 1) {
    const hours = Math.max(1, Math.round(value * 24));
    return hours === 1 ? '1 hora' : `${hours} horas`;
  }
  const rounded = Math.round(value * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 0.05) {
    const days = Math.round(rounded);
    return days === 1 ? '1 día' : `${days} días`;
  }
  return `${rounded.toFixed(1)} días`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function previewFromStats(stats, rawCount) {
  const total = Number(stats?.total) || 0;
  const keepMin = Number(stats?.keepMin) || 1;
  const maxDelete = Math.max(0, total - keepMin);
  const deleteCount = Math.min(Math.max(0, Math.floor(Number(rawCount) || 0)), maxDelete);
  const buckets = stats?.buckets || [];
  const oldestAt = stats?.oldestAt ? new Date(stats.oldestAt) : null;
  const newestAt = stats?.newestAt ? new Date(stats.newestAt) : null;
  const span = (from, to) => {
    if (!from || !to) return 0;
    const ms = to.getTime() - from.getTime();
    return ms <= 0 ? 0 : ms / (24 * 60 * 60 * 1000);
  };

  if (!total || !oldestAt || !newestAt || deleteCount <= 0) {
    return {
      total,
      deleteCount: 0,
      keepCount: total,
      maxDelete,
      deleteDays: 0,
      keepDays: span(oldestAt, newestAt),
      deleteFrom: null,
      deleteUntil: null,
      keepFrom: oldestAt,
      keepUntil: newestAt,
    };
  }

  let left = deleteCount;
  let deleteUntil = null;
  let keepFrom = newestAt;

  for (let i = 0; i < buckets.length; i += 1) {
    const bucket = buckets[i];
    const count = Number(bucket.count) || 0;
    const first = bucket.first ? new Date(bucket.first) : null;
    const last = bucket.last ? new Date(bucket.last) : null;
    if (left <= 0) {
      keepFrom = first || keepFrom;
      break;
    }
    if (left >= count) {
      left -= count;
      deleteUntil = last || first;
      if (i === buckets.length - 1) keepFrom = newestAt;
      continue;
    }
    const ratio = count ? left / count : 1;
    if (first && last) {
      deleteUntil = new Date(first.getTime() + (last.getTime() - first.getTime()) * ratio);
    } else {
      deleteUntil = last || first;
    }
    keepFrom = deleteUntil;
    left = 0;
    break;
  }

  return {
    total,
    deleteCount,
    keepCount: total - deleteCount,
    maxDelete,
    deleteDays: span(oldestAt, deleteUntil),
    keepDays: span(keepFrom, newestAt),
    deleteFrom: oldestAt,
    deleteUntil,
    keepFrom,
    keepUntil: newestAt,
  };
}

function countForDays(stats, days) {
  const buckets = stats?.buckets || [];
  if (!buckets.length) return 0;
  const take = Math.max(1, Number(days) || 0);
  let count = 0;
  for (let i = 0; i < Math.min(take, buckets.length); i += 1) {
    count += Number(buckets[i].count) || 0;
  }
  const maxDelete = Math.max(0, (Number(stats?.total) || 0) - (Number(stats?.keepMin) || 1));
  return Math.min(count, maxDelete);
}

export default function WaitTimesFlushManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [flushing, setFlushing] = useState(false);
  const [stats, setStats] = useState(null);
  const [deleteCount, setDeleteCount] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get('/wait_times/flush/stats');
    setStats(data);
    setDeleteCount(0);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar wait_times',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const preview = useMemo(
    () => previewFromStats(stats, deleteCount),
    [stats, deleteCount]
  );

  const maxDelete = preview.maxDelete || 0;
  const sliderStep = Math.max(1, Math.round(maxDelete / 200) || 1);

  const setCount = (value) => {
    const next = Math.min(maxDelete, Math.max(0, Math.floor(Number(value) || 0)));
    setDeleteCount(next);
  };

  const runFlush = async () => {
    if (!preview.deleteCount) return;
    try {
      setFlushing(true);
      const { data } = await api.post('/wait_times/flush', { count: preview.deleteCount });
      toast.current?.show({
        severity: 'success',
        summary: 'Listo',
        detail: `Se borraron ${formatCount(data.deleted)} snapshots viejos`,
      });
      if (data.after) {
        setStats(data.after);
        setDeleteCount(0);
      } else {
        await load();
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'No se pudo hacer flush',
      });
    } finally {
      setFlushing(false);
    }
  };

  const confirmFlush = () => {
    if (!preview.deleteCount) return;
    confirmDialog({
      header: 'Borrar snapshots viejos',
      message: `Vas a borrar ${formatCount(preview.deleteCount)} reportes (${formatDays(preview.deleteDays)}, ${formatDate(preview.deleteFrom)} → ${formatDate(preview.deleteUntil)}). Quedan ${formatCount(preview.keepCount)} (${formatDays(preview.keepDays)}). Esto no se puede deshacer.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Borrar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: runFlush,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-content-center mt-5">
        <ProgressSpinner />
      </div>
    );
  }

  const empty = !stats?.total;

  return (
    <div className="wtf-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <header className="wtf-header">
        <div>
          <p className="wtf-kicker">Community & Mobile</p>
          <h2 className="wtf-title">Flush wait times</h2>
          <p className="wtf-lead">
            Borra snapshots viejos de CBP. El más reciente no se toca, para que app y web sigan con el tiempo actual.
          </p>
        </div>
      </header>

      <section className="wtf-stats">
        <article className="wtf-stat">
          <small>Hay ahora</small>
          <strong>{formatCount(stats?.total)}</strong>
          <span>snapshots</span>
        </article>
        <article className="wtf-stat">
          <small>Historial</small>
          <strong>{formatDays(stats?.spanDays)}</strong>
          <span>
            {formatDate(stats?.oldestAt)} → {formatDate(stats?.newestAt)}
          </span>
        </article>
        <article className="wtf-stat">
          <small>Se quedan mínimo</small>
          <strong>1</strong>
          <span>el más nuevo</span>
        </article>
      </section>

      {empty ? (
        <p className="wtf-empty">No hay wait_times para borrar.</p>
      ) : (
        <>
          <section className="wtf-card">
            <h3>¿Cuántos viejos borrar?</h3>
            <div className="wtf-presets">
              {DAY_PRESETS.map((days) => {
                const count = countForDays(stats, days);
                if (!count) return null;
                return (
                  <button
                    key={days}
                    type="button"
                    className={`wtf-chip ${deleteCount === count ? 'is-on' : ''}`}
                    onClick={() => setCount(count)}
                  >
                    {days} días
                  </button>
                );
              })}
              <button
                type="button"
                className="wtf-chip"
                onClick={() => setCount(Math.floor(maxDelete / 2))}
              >
                Mitad
              </button>
            </div>

            <div className="wtf-slider-wrap">
              <Slider
                value={deleteCount}
                min={0}
                max={maxDelete}
                step={sliderStep}
                onChange={(e) => setCount(e.value)}
              />
              <InputNumber
                value={deleteCount || null}
                onChange={(e) => setCount(e.value)}
                onValueChange={(e) => setCount(e.value)}
                min={0}
                max={maxDelete}
                allowEmpty
                useGrouping={false}
                placeholder="Cantidad"
                inputClassName="wtf-count-input"
              />
            </div>
            <p className="wtf-hint">
              Máximo {formatCount(maxDelete)}. Siempre queda el snapshot más reciente.
            </p>
          </section>

          <section className="wtf-split">
            <article className="wtf-result is-delete">
              <small>Vas a borrar</small>
              <strong>{formatCount(preview.deleteCount)}</strong>
              <p className="wtf-days">{formatDays(preview.deleteDays)}</p>
              <span>
                {preview.deleteCount
                  ? `${formatDate(preview.deleteFrom)} → ${formatDate(preview.deleteUntil)}`
                  : 'Nada todavía'}
              </span>
            </article>
            <article className="wtf-result is-keep">
              <small>Van a quedar</small>
              <strong>{formatCount(preview.keepCount)}</strong>
              <p className="wtf-days">{formatDays(preview.keepDays)}</p>
              <span>
                {formatDate(preview.keepFrom)} → {formatDate(preview.keepUntil)}
              </span>
            </article>
          </section>

          <div className="wtf-bar">
            <Button
              label={`Flush ${formatCount(preview.deleteCount)}`}
              icon="pi pi-trash"
              className="p-button-danger wtf-flush-btn"
              disabled={!preview.deleteCount || flushing}
              loading={flushing}
              onClick={confirmFlush}
            />
          </div>
        </>
      )}
    </div>
  );
}
